import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { HRM1030_R01FileDto, HRM1030_R01TrainingDto } from './Dto_HRM1030_R01';

/** 교육 이력과 첨부 중 마지막으로 작업한 그리드이다. 액션바 삭제·Excel의 대상 그리드를 정할 때 쓴다. */
export type HRM1030_R01GridType = 'training' | 'file';

/** 법정의무교육 교육구분 코드 (공통코드 TRAIN_TYPE) */
export const HRM1030_R01_LAW_TRAIN_TYPE = 'T3';
/** 법정 이수 건수가 이 값보다 작으면 빨간색으로 표시한다(명세 기준 "2건 미만"). */
export const HRM1030_R01_LAW_MIN_COUNT = 2;
/** 산업안전 이수시간이 이 값보다 작으면 경고색으로 표시한다(프로토타입 기준 6시간). */
export const HRM1030_R01_SAFETY_MIN_HOURS = 6;
/** 산업안전 요약 값 집계 대상 교육과정명 시작 문구 */
export const HRM1030_R01_SAFETY_PREFIX = '산업안전';
/** 첨부 허용 확장자. WEBP는 공통 파일 서비스가 지원하지 않아 뺐다. */
export const HRM1030_R01_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'pdf'];
/** 파일 선택 창의 accept 값 */
export const HRM1030_R01_FILE_ACCEPT = HRM1030_R01_ALLOWED_EXTENSIONS.map(extension => `.${extension}`).join(',');
/** 첨부 최대 크기 10MB(byte) */
export const HRM1030_R01_MAX_FILE_SIZE = 10 * 1024 * 1024;

/** 공통코드명이 없으면 원래 코드값을 표시한다. */
export const formatHRM1030_R01Code = (list: SysCodeDto[], value: string): string =>
    list.find(item => item.minorCd === value)?.codeNm || value;

/**
 * 숫자를 천 단위 구분 문자열로 바꾼다.
 * @param value 숫자 값
 * @param digits 소수 자릿수 (기본 0)
 * @returns 표시 문자열. 값이 없으면 빈 문자열
 */
export const formatHRM1030_R01Number = (value: number | string | null | undefined, digits = 0): string => {
    if (value === null || value === undefined || value === '') return '';
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return '';
    return numberValue.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/**
 * 오늘 날짜를 YYYY-MM-DD로 만든다. 신규 교육 행의 시작·종료일 기본값에 쓴다.
 * @returns 오늘 날짜 문자열
 */
export const getHRM1030_R01Today = (): string => {
    const today = new Date();
    // padStart: 월·일이 한 자리면 앞에 0을 붙인다.
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

/**
 * 파일명에서 소문자 확장자를 꺼낸다.
 * @param fileNm 파일명
 * @returns 점(.)을 뺀 소문자 확장자. 없으면 빈 문자열
 */
export const getHRM1030_R01Extension = (fileNm: string): string => {
    const dotIndex = fileNm.lastIndexOf('.');
    return dotIndex < 0 ? '' : fileNm.substring(dotIndex + 1).toLowerCase();
};

/**
 * 첨부가 PDF인지 확인한다. MIME 형식이 없으면 확장자로 판단한다.
 * @param file 첨부 행
 * @returns PDF이면 true
 */
export const isHRM1030_R01Pdf = (file: Pick<HRM1030_R01FileDto, 'fileType' | 'fileNm'>): boolean =>
    file.fileType === 'application/pdf' || getHRM1030_R01Extension(file.fileNm) === 'pdf';

/**
 * 확장자로 MIME 형식을 정한다. 서버 저장 값과 같은 규칙이다.
 * @param fileNm 파일명
 * @returns MIME 형식
 */
export const getHRM1030_R01MimeType = (fileNm: string): string => {
    const extension = getHRM1030_R01Extension(fileNm);
    if (extension === 'pdf') return 'application/pdf';
    if (extension === 'png') return 'image/png';
    if (extension === 'gif') return 'image/gif';
    return 'image/jpeg';
};

/**
 * 끌어놓기·파일 선택으로 받은 파일의 형식·크기 위반 사유를 반환한다.
 * accept 속성만으로는 끌어놓기를 막지 못해 직접 검사한다.
 * @param file 브라우저 File 객체
 * @returns 위반 사유('형식' 또는 '10MB 초과'). 통과하면 undefined
 */
export const validateHRM1030_R01File = (file: File): string | undefined => {
    if (!HRM1030_R01_ALLOWED_EXTENSIONS.includes(getHRM1030_R01Extension(file.name))) return '형식';
    if (file.size > HRM1030_R01_MAX_FILE_SIZE) return '10MB 초과';
    return undefined;
};

/**
 * 파일을 읽어 data URL 접두사를 뗀 Base64로 만든다(FileForm과 같은 방식).
 * @param file 브라우저 File 객체
 * @returns Base64 문자열 Promise
 */
export const readHRM1030_R01FileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    // 정규식: 맨 앞의 "data:형식;base64," 접두사를 찾아 지운다.
    reader.onloadend = () => resolve(String(reader.result || '').replace(/^data:.*?;base64,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

/**
 * Blob을 data URL 접두사를 뗀 Base64로 만든다. 저장된 이미지를 ImagePreviewModal에 넘길 때 쓴다.
 * @param blob 다운로드한 파일 Blob
 * @returns Base64 문자열 Promise
 */
export const readHRM1030_R01BlobAsBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || '').replace(/^data:.*?;base64,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
});

/** 선택 사원 요약 값(이수시간·교육비·환급액·법정·산업안전) 계산 결과이다. */
export interface HRM1030_R01Summary {
    completeHours: number; // 이수(Y) 교육시간 합계
    costAmt: number; // 교육비 합계
    refundAmt: number; // 환급액 합계
    lawCount: number; // 법정의무(T3)·이수(Y) 건수
    safetyHours: number; // 산업안전으로 시작하는 법정의무·이수 교육시간 합계
}

/**
 * D 표시 행을 뺀 교육 목록으로 요약 값을 계산한다.
 * @param list 편집 중인 교육 목록
 * @param deletedIds D 표시한 교육 valueIndex 문자열 목록
 * @returns 요약 값
 */
export const getHRM1030_R01Summary = (list: HRM1030_R01TrainingDto[], deletedIds: string[]): HRM1030_R01Summary => {
    const liveRows = list.filter(row => !deletedIds.includes(String(row.valueIndex)));
    const completedRows = liveRows.filter(row => row.completeYn === 'Y');
    const lawRows = completedRows.filter(row => row.trainType === HRM1030_R01_LAW_TRAIN_TYPE);
    /** 목록의 숫자 필드 합계 */
    const sum = (rows: HRM1030_R01TrainingDto[], pick: (row: HRM1030_R01TrainingDto) => number) =>
        rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);
    return {
        completeHours: sum(completedRows, row => row.trainHours),
        costAmt: sum(liveRows, row => row.costAmt),
        refundAmt: sum(liveRows, row => row.refundAmt),
        lawCount: lawRows.length,
        safetyHours: sum(lawRows.filter(row => row.trainNm.startsWith(HRM1030_R01_SAFETY_PREFIX)), row => row.trainHours),
    };
};

/**
 * 저장할 교육 행을 검증한다. 문장은 백엔드 검증과 같다.
 * @param rows D 표시를 뺀 신규·수정 교육 행
 * @returns 첫 번째 오류 문장. 문제가 없으면 undefined
 */
export const validateHRM1030_R01SaveData = (rows: HRM1030_R01TrainingDto[]): string | undefined => {
    for (const row of rows) {
        if (!row.trainNm.trim()) return '교육과정명을 입력해주세요.';
        if (row.trainNm.length > 100 || row.institution.length > 100 || row.certNo.length > 50 || row.remark.length > 1000)
            return `교육 항목의 길이가 초과되었습니다. > ${row.trainNm}`;
        if (row.startDt && row.endDt && row.endDt < row.startDt) return `종료일이 시작일보다 빠릅니다: ${row.trainNm}`;
        if (Number(row.refundAmt) > Number(row.costAmt)) return `환급액이 교육비보다 큽니다: ${row.trainNm}`;
    }
    return undefined;
};
